import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  getUsage,
  selectUsage,
  selectError,
  selectLoading,
  selectHasAICredits,
  selectCanCreateListing,
} from 'src/redux/slices/plan';

// ----------------------------------------------------------------------

export function usePlan () {
  const dispatch = useDispatch();
  const usage = useSelector(selectUsage);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const canCreateListing = useSelector(selectCanCreateListing);
  const hasAICredits = useSelector(selectHasAICredits);

  const refreshUsage = useCallback(() => {
    dispatch(getUsage());
  }, [dispatch]);

  return {
    usage,
    loading,
    error,
    refreshUsage,
    canCreateListing,
    hasAICredits,
    canCreateDraft: (() => {
      if (!usage) return true;
      const draftLimit = usage.limits?.draftListings;
      // V1 plans have draftListings: null (unlimited)
      if (draftLimit === null || draftLimit === undefined) return true;
      return (usage.usage?.draftListings || 0) < draftLimit;
    })(),
    getUsagePercentage: (resourceType) => {
      if (!usage) return 0;
      if (resourceType === 'listings') {
        return (usage.usage.activeListings / usage.limits.activeListings) * 100;
      }
      if (resourceType === 'draftListings') {
        const draftLimit = usage.limits?.draftListings;
        if (!draftLimit) return 0; // V1/Power 5K+: unlimited drafts
        return ((usage.usage?.draftListings || 0) / draftLimit) * 100;
      }
      if (resourceType === 'aiCredits') {
        const usedCredits = usage.limits.aiCreditsPerMonth - usage.usage.aiCredits;
        return (usedCredits / usage.limits.aiCreditsPerMonth) * 100;
      }
      return 0;
    },
    isNearLimit: (resourceType) => {
      if (!usage) return false;
      let percentage;
      if (resourceType === 'listings') {
        percentage = (usage.usage.activeListings / usage.limits.activeListings) * 100;
      } else if (resourceType === 'draftListings') {
        const draftLimit = usage.limits?.draftListings;
        if (!draftLimit) return false; // V1/Power 5K+: unlimited drafts
        percentage = ((usage.usage?.draftListings || 0) / draftLimit) * 100;
      } else {
        percentage = ((usage.limits.aiCreditsPerMonth - usage.usage.aiCredits) / usage.limits.aiCreditsPerMonth) * 100;
      }
      return percentage >= 80;
    },
    isAtLimit: (resourceType) => {
      if (!usage) return false;
      if (resourceType === 'listings') {
        return usage.usage.activeListings >= usage.limits.activeListings;
      }
      if (resourceType === 'draftListings') {
        const draftLimit = usage.limits?.draftListings;
        if (!draftLimit) return false; // V1/Power 5K+: unlimited drafts
        return (usage.usage?.draftListings || 0) >= draftLimit;
      }
      if (resourceType === 'aiCredits') {
        return (usage.usage.aiCredits + (usage.usage.aiCreditsAddOn || 0)) <= 0;
      }
      return false;
    },
  };
}
